import type { FunctionComponent } from 'react';

import { useEffect, useContext } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router';
import { useDisclosure } from '@mantine/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Anchor, Box, Button, Grid, Image } from '@mantine/core';

import { ErrorState } from 'components/Errors/ErrorState';
import LoadingCard from 'components/LoadingCard';
import { sortByItemTypeOrdinal } from 'helpers/menuManagement';
import { useMenuPreview } from 'hooks/useMenuManagementMenuPreview';
import { GENERATED_MENU_SOURCE, MENU_UPLOAD_FEEDBACK_URL } from 'constants/menuManagement';
import { POS_MENU_SETTINGS_ROUTE } from 'constants/path';
import MenuInformation from 'components/MenuManagement/MenuInformation';
import { MenuBanner } from 'components/MenuManagement/MenuPreview/MenuBanner';
import CategoryContainer from 'components/MenuManagement/MenuPreview/CategoryContainer';
import { MenuManagementPreviewItemModal } from 'components/Modals/MenuManagementPreviewItemModal';
import { HoursModal } from 'components/Modals/HoursModal';
import { PosImportedBanner } from 'containers/PosMenuSettings';
import DashBanner from 'primitives/DashBanner/DashBanner';

import halftonePot from 'assets/images/halftone-pot.png';

export const MenuContent: FunctionComponent = () => {
  const { addMainItem, genericMenuError, getSortedChildren, isLoading, menu, setAddMainItem } =
    useMenuPreview();

  const { menuUploadWizard } = useFlags();
  const { previewMenu } = useContext(MenuInformation.Context);
  const menuUuid = previewMenu?.menuUuid;
  const isGeneratedMenu = previewMenu?.menuSource === GENERATED_MENU_SOURCE;
  const isPosMenuSettingsRoute = !!useMatch(POS_MENU_SETTINGS_ROUTE);

  const navigate = useNavigate();
  const { restaurantId } = useParams();

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [hoursModalOpened, { open: openHoursModal, close: closeHoursModal }] = useDisclosure(false);
  const generatedBannerKey = `menuBuilderBannerDismissed_${menuUuid}`;
  const [showGeneratedBanner, { close: dismissGeneratedBanner }] = useDisclosure(
    localStorage.getItem(generatedBannerKey) !== 'true',
  );

  const categories = Object.keys(menu?.categories ?? {}).sort(
    sortByItemTypeOrdinal(menu, 'categories'),
  );

  useEffect(() => {
    if (addMainItem) {
      openModal();
    }
  }, [addMainItem, openModal]);

  if (menu?.hasError || genericMenuError) {
    return <ErrorState data-cy="menu-error" errorMessage="This menu is currently unavailable." />;
  }

  if (!previewMenu) {
    return (
      <ErrorState data-cy="preview-menu-error" errorMessage="Menu preview data is not available." />
    );
  }

  const hoursMenu = {
    name: menu?.name,
    itemHours: previewMenu.itemHours,
    specialHours: previewMenu.specialHours,
    deliveryHours: previewMenu.deliveryHours,
    deliveryMatchesPickup: previewMenu.deliveryMatchesPickup,
  };

  if (!menuUuid || !menu || isLoading) {
    return (
      <Box mt="xl2">
        <LoadingCard />
      </Box>
    );
  }

  const editUrl = `/restaurant/${restaurantId}/menu/edit?merchantId=${previewMenu.merchantID}&menuId=${menuUuid}`;

  return (
    <>
      <MenuBanner
        hoursButton={
          <Button variant="outline" size="md" onClick={openHoursModal}>
            Hours
          </Button>
        }
        editButton={
          <Button size="md" onClick={() => void navigate(editUrl)}>
            Edit
          </Button>
        }
      />
      <PosImportedBanner />
      {menuUploadWizard && isGeneratedMenu && showGeneratedBanner && !isPosMenuSettingsRoute && (
        <Box mb="xs">
          <DashBanner
            variant="green"
            message={
              <>
                This menu was created with &apos;Menu Builder&apos;. You can make updates to this
                menu before you publish. Want to share your thoughts?{' '}
                <Anchor href={MENU_UPLOAD_FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
                  Leave feedback
                </Anchor>
              </>
            }
            icon={
              <Image src={halftonePot} alt="" w={40} h={40} style={{ mixBlendMode: 'multiply' }} />
            }
            withCloseButton
            onClose={() => {
              localStorage.setItem(generatedBannerKey, 'true');
              dismissGeneratedBanner();
            }}
          />
        </Box>
      )}
      <CategoryContainer categories={categories} />
      {addMainItem && (
        <MenuManagementPreviewItemModal
          opened={modalOpened}
          onClose={closeModal}
          mainItem={addMainItem}
          menu={menu}
          getSortedChildren={getSortedChildren}
          onActionFinish={() => setAddMainItem(null)}
        />
      )}
      <HoursModal menu={hoursMenu} opened={hoursModalOpened} onClose={closeHoursModal} />
    </>
  );
};

export const Menu: FunctionComponent = () => {
  const { hasError: configHasError } = useMenuPreview();

  if (configHasError) {
    return (
      <ErrorState
        data-cy="config-error"
        errorMessage="We encountered an error when trying to load this storefront."
      />
    );
  }

  return (
    <Grid>
      <Grid.Col span={12}>
        <MenuContent />
      </Grid.Col>
    </Grid>
  );
};
